import classNames from 'classnames';
import styles from './TileCard.module.scss';
import { UI_Heading } from 'ui/components/1-atoms/Heading/Heading';
import { UI_Link } from 'ui/components/1-atoms/Action/Link/Link';
import { UI_Picture } from 'ui/components/1-atoms/Media/Picture/Picture';
import { UI_Kicker, UI_KickerProps } from 'ui/components/1-atoms/Kicker/Kicker';
import { LinkType } from 'helpers/types';
import { UI_Player } from 'ui/components/1-atoms/Media/Player/Player';
import { useInView } from 'react-intersection-observer';

export interface UI_TileCardProps {
	className?: string;
	image?: {
		url: string;
		altText: string;
		focalPoint?: { left: number; top: number };
		fileType?: string;
	};
	video?: {
		url: string;
		poster?: string;
		autoplay?: boolean;
	};
	link?: LinkType;
	kicker?: UI_KickerProps;
	heading?: string;
	text?: string;
	imageCropping?: 'cover' | 'contain';
	borderNone?: boolean;
	noPaddingTop?: boolean;
}

export const UI_TileCard: React.FC<UI_TileCardProps> = ({
	className,
	image,
	video,
	link,
	kicker,
	heading,
	text,
	imageCropping = 'cover',
	borderNone,
	noPaddingTop,
}) => {
	const { ref, inView } = useInView({
		threshold: 0.8,
		triggerOnce: false,
	});
	return (
		<div
			ref={ref}
			className={classNames(
				styles.TileCard,
				{ [styles[`TileCard___noPaddingTop`]]: noPaddingTop },
				{ [styles.TileCard___borderNone]: borderNone === true },
				className,
			)}
		>
			<div className={styles.TileCard_textbox}>
				{kicker && <UI_Kicker className={styles.TileCard_kicker} {...kicker}></UI_Kicker>}
				{heading && (
					<UI_Heading className={styles.TileCard_heading} headingLevel="h2">
						{heading}
					</UI_Heading>
				)}
				{text && <div className={styles.TileCard_text} dangerouslySetInnerHTML={{ __html: text }}></div>}
				{link && (
					<div>
						<UI_Link href={link.url} target={link.target} className={styles.TileCard_link} underline={true}>
							{link.name}
						</UI_Link>
					</div>
				)}
			</div>
			{image && image.url && (
				<div className={styles.TileCard_media}>
					<span className={styles.TileCard_mediabox}>
						{video && video.url ? (
							<UI_Player
								cover
								url={video.url}
								isLight={(video.autoplay && !inView) || !video.autoplay ? true : false}
								playing={inView ? true : false}
								muted={true}
								loop={true}
								controls={false}
								poster={image.url}
								playIcon={
									!video.autoplay ? (
										inView ? (
											<div className={styles.TileCard_playIconContainer}>
												<span className={styles.TileCard_playIcon}></span>
											</div>
										) : (
											<span></span>
										)
									) : (
										<span></span>
									)
								}
							/>
						) : (
							image &&
							image.url && (
								<UI_Picture
									aspectRatio={0.5625}
									url={image.url}
									altText={image.altText}
									focalPoint={image.focalPoint}
									sizes="(max-width: 768px) 100vw, 460px"
									imageCropping={imageCropping}
									fileType={image.fileType}
								/>
							)
						)}
					</span>
				</div>
			)}
		</div>
	);
};
